import React, { useCallback, useEffect, useState } from 'react';
import Helmet from 'react-helmet';
import { useIntl } from 'react-intl';
import { showFormErrors } from '../../actions/login-actions';
import { useAppDispatch, useAppSelector } from '../../stores/redux.store';
import LoginForm from './components/LoginForm';
import SocialLoginForm from '../../components/SocialLoginForm';
import Whitebox from '../../components/WhiteboxPage';
import { LoginFieldError } from '../../types';
import { LoginPageMessages } from '../../pages/login/messages';
import { useLoginState } from '../../utilities/hooks/useLoginState';

import Footer from '../../pages/login/components/Footer';
import Header from '../../pages/login/components/Header';
import MaybeSocialLoginPanel from '../../pages/login/components/MaybeSocialLoginPanel';
import WhiteboxFooter from '../../pages/login/components/WhiteboxFooter';
import { loginPageId as defaultPageId } from '../../pages/login/constants';
import { useEmailFromSearchParams } from '../../pages/login/hooks/utility';
import { useLogin } from '../../pages/login/hooks/useLogin';
import { useLoginSubmission } from '../../pages/login/hooks/useLoginSubmission';
import { useSocialLogin } from '../../pages/login/hooks/useSocialLogin';
import { useGoogleOneTap } from '../../pages/login/hooks/useGoogleOneTap';
import { checkFeatureGate } from '../../utilities/feature-flags/feature-gate';
import { AidFrontendFeatureGate } from '../../providers/FeatureGates';

export const StatelessLoginPageId = defaultPageId + '_isolated_cloud';

const LoginPage = () => {
  const { state } = useLoginState();

  const { oidcContext } = state || {};

  const dispatch = useAppDispatch();

  const isNewV2CheckUsernameFlowEnabled = checkFeatureGate(
    AidFrontendFeatureGate.ENABLE_NEW_V2_CHECK_USERNAME_IC_FLOW
  );

  const {
    isPasswordlessAllowed,
    checkUsernameError,
    error: loginError,
    redirectType,
    isRestrictedEmail,
    resetLogin,
    infoCode,
    rememberMeEnabled,
    isRememberMeChecked,
    onToggleRememberMeCheckbox,
    isUsernameChecked,
    isCheckUsernameLoading,
    checkUsername,
    v2Accounts,
  } = useLogin({ useV2CheckUsernameFlow: isNewV2CheckUsernameFlowEnabled });

  const {
    isLoading,
    onLogin: handleLogIn,
    onPasswordlessLogin: handlePasswordlessLogIn,
  } = useLoginSubmission();

  const {
    onAppleLogin,
    onSlackLogin,
    onMicrosoftLogin,
    onGoogleSocialLogin,
    onDismissGoogleOneTap,
    socialLoginEligibility,
    envSupportsSocialLogin,
    isSocialBlocked,
  } = useSocialLogin();

  const { isMobileApp, isEmbedded, screenSize } = useAppSelector(state => state.microbranding);

  const isLoginSecondStep = isUsernameChecked;

  const { formatMessage } = useIntl();

  const emailFromSearchParams = useEmailFromSearchParams();

  const [email, setEmail] = useState(() => emailFromSearchParams || '');

  // Sync state with query params
  // In the case when the query params change after the component is first rendered
  useEffect(() => {
    if (emailFromSearchParams) {
      setEmail(emailFromSearchParams);
    }
  }, [emailFromSearchParams]);

  useGoogleOneTap({ email });

  const { highlightedSocialProvider = null, isEligibleForSocialLoginOnly = null } =
    socialLoginEligibility || {};

  const showSocialLoginOnly =
    isLoginSecondStep &&
    isEligibleForSocialLoginOnly &&
    highlightedSocialProvider &&
    envSupportsSocialLogin;

  const handleGoogleSocialLogin = useCallback(() => {
    if (envSupportsSocialLogin) {
      onGoogleSocialLogin(email);
    }
  }, [email, onGoogleSocialLogin, envSupportsSocialLogin]);

  const handleMicrosoftLogin = useCallback(() => {
    if (envSupportsSocialLogin) {
      onMicrosoftLogin(email);
    }
  }, [email, onMicrosoftLogin, envSupportsSocialLogin]);

  const handleAppleLogin = useCallback(() => {
    if (envSupportsSocialLogin) {
      onAppleLogin(email);
    }
  }, [email, onAppleLogin, envSupportsSocialLogin]);

  const handleSlackLogin = useCallback(() => {
    if (envSupportsSocialLogin) {
      onSlackLogin(email);
    }
  }, [email, onSlackLogin, envSupportsSocialLogin]);

  const handleValidationError = useCallback(
    (errors: LoginFieldError) => dispatch(showFormErrors(errors)),
    [dispatch]
  );

  const handleContinue = (email: string) => {
    checkUsername(email);
  };

  const handleFormClick = useCallback(() => {
    if (envSupportsSocialLogin && screenSize === 'mobile') {
      onDismissGoogleOneTap();
    }
  }, [onDismissGoogleOneTap, screenSize, envSupportsSocialLogin]);

  const handleEmailChange = useCallback((email: string) => {
    setEmail(email);
  }, []);

  return (
    <>
      <Helmet>
        <title>{formatMessage(LoginPageMessages.headTitle)}</title>
      </Helmet>
      <Whitebox
        pageId={StatelessLoginPageId}
        header={
          <Header
            highlightedSocialProvider={highlightedSocialProvider}
            showSocialLoginOnly={showSocialLoginOnly}
          />
        }
        footer={<Footer oidcContext={oidcContext} isEmbedded={isEmbedded} />}
        whiteboxFooter={
          <WhiteboxFooter
            showSocialLoginOnly={showSocialLoginOnly}
            isMobileApp={isMobileApp}
            oidcContext={oidcContext}
          />
        }>
        {showSocialLoginOnly ? (
          <SocialLoginForm
            email={email}
            highlightedSocialProvider={highlightedSocialProvider}
            handleGoogleLogin={handleGoogleSocialLogin}
            handleMicrosoftLogin={handleMicrosoftLogin}
            handleAppleLogin={handleAppleLogin}
            handleSlackLogin={handleSlackLogin}
          />
        ) : (
          <>
            <LoginForm
              application={'support'}
              redirectType={!isLoginSecondStep ? redirectType : null}
              isRedirecting={redirectType !== null}
              isSecondStep={isLoginSecondStep}
              isPasswordlessAllowed={isPasswordlessAllowed}
              isLoading={isLoading}
              isCheckUsernameLoading={isCheckUsernameLoading}
              onContinue={handleContinue}
              onLogIn={handleLogIn}
              onPasswordlessLogIn={handlePasswordlessLogIn}
              error={loginError}
              info={infoCode}
              fieldErrors={{
                email: checkUsernameError,
              }}
              email={email}
              onValidationError={handleValidationError}
              onEditEmail={resetLogin}
              onFormClick={handleFormClick}
              restrictedEmail={isRestrictedEmail}
              pageId={StatelessLoginPageId}
              onEmailChange={handleEmailChange}
              showRememberMeCheckbox={rememberMeEnabled}
              rememberMeChecked={isRememberMeChecked}
              onToggleRememberMeCheckbox={onToggleRememberMeCheckbox}
              v2Accounts={v2Accounts || []}
              isNewV2CheckUsernameFlow={isNewV2CheckUsernameFlowEnabled}
            />
            <MaybeSocialLoginPanel
              isEmbedded={isEmbedded}
              envSupportsSocialLogin={envSupportsSocialLogin}
              isSocialBlocked={isSocialBlocked}
              onSlackLogin={handleSlackLogin}
              onAppleLogin={handleAppleLogin}
              onMicrosoftLogin={handleMicrosoftLogin}
              onGoogleLogin={handleGoogleSocialLogin}
            />
          </>
        )}
      </Whitebox>
    </>
  );
};

export default LoginPage;
